import {Checkbox, CheckboxProps, createStyles, Flex} from '@mantine/core';
import React, {RefObject, useEffect, useRef} from 'react';

import HelpButton from 'help/HelpButton';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import {InputChangeEvent} from 'util/Types';

export interface CheckboxInputProps {
  checked: boolean;
  className?: string;
  // id is required for now due to the htmlFor implementation detail, but in the future we could wrap the
  // and text into a <label> to avoid the need for that.
  id: string;
  indeterminate?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  label: string | JSX.Element;
  labelStyle?: LabelStyle;
  helpText?: string | JSX.Element;
  disabled?: boolean;
  onChange: (e: InputChangeEvent) => void;
  dataTestAttrs?: DataTestAttrs;
}

const useStyles = createStyles(() => ({
  checkboxLabel: {
    // match old spacing
    paddingLeft: 6,
    lineHeight: '16px',
    // override benchling.css styles
    display: 'unset',
    marginBottom: 'unset',
    fontWeight: 'normal',
  },
  checkboxInner: {
    // center box with larger line height
    marginTop: 2,
  },
  helpButton: {
    verticalAlign: 'text-top',
  },
}));

/**
 * @deprecated Use Mantine's `Checkbox` directly instead.
 */
function CheckboxInput(props: CheckboxInputProps): JSX.Element {
  const {classes, cx} = useStyles();

  // You can't conditionally use hooks, so we always do a useRef and just don't use it if we're passed a
  // ref in props.
  const internalRef = useRef<HTMLInputElement | null>(null);
  const ref = props.inputRef ?? internalRef;
  useEffect(() => {
    if (ref.current != null && props.indeterminate != null) {
      ref.current.indeterminate = props.indeterminate;
    }
  }, [ref, props.indeterminate]);

  const labelProps: Partial<CheckboxProps> = {};
  if (props.labelStyle === LabelStyle.HIDDEN) {
    labelProps['aria-label'] = props.label as string;
  } else if (props.labelStyle === LabelStyle.LABEL || !props.labelStyle) {
    labelProps.label = props.label;
  } else if (props.labelStyle === LabelStyle.SUBLABEL) {
    labelProps.description = props.label;
  }

  return (
    <Flex className={props.className} {...getTestProps({component: 'CheckboxInput', element: 'root'})}>
      <Checkbox
        size="14px"
        classNames={{label: classes.checkboxLabel, inner: classes.checkboxInner}}
        checked={props.checked}
        indeterminate={props.indeterminate}
        id={props.id}
        onChange={props.onChange}
        disabled={props.disabled}
        ref={ref}
        {...labelProps}
        {...getTestProps(props.dataTestAttrs)}
      />
      {props.helpText && (
        <HelpButton
          renderContent={() => <p>{props.helpText}</p>}
          iconClassName={cx('u-mini-spacing-left', classes.helpButton)}
          interaction={FloatingInteraction.HOVER}
        />
      )}
    </Flex>
  );
}

CheckboxInput.defaultProps = {
  disabled: false,
};

export default CheckboxInput;
